/** Fiche client, particulier ou societe (Franck, 08/10/2026). Exigee a la
 *  premiere demande de recharge : c'est l'identite imprimee sur le bon de
 *  commande et la facture. Modifiable ensuite dans Parametres. */
import { useEffect, useState } from "react";

import { api, type FicheClient as Fiche } from "../api";
import { Chargement, Erreur } from "../components/ui";

const VIDE: Fiche = {
  type: "societe", prenom: null, nom: null, raison_sociale: null, siren: null, tva: null, adresse: null,
  code_postal: null, ville: null, pays: "FR", tel_mobile: null, tel_fixe: null, email_facturation: null,
  complete: false,
};

/** Pays proposes (08/10/2026 : « le pays doit etre une liste de choix ») :
 *  l'Union europeenne d'abord (la TVA intracommunautaire en depend), puis
 *  les voisins hors UE les plus probables. */
const UE: [string, string][] = [
  ["FR", "France"], ["DE", "Allemagne"], ["AT", "Autriche"], ["BE", "Belgique"], ["BG", "Bulgarie"],
  ["CY", "Chypre"], ["HR", "Croatie"], ["DK", "Danemark"], ["ES", "Espagne"], ["EE", "Estonie"],
  ["FI", "Finlande"], ["GR", "Grèce"], ["HU", "Hongrie"], ["IE", "Irlande"], ["IT", "Italie"],
  ["LV", "Lettonie"], ["LT", "Lituanie"], ["LU", "Luxembourg"], ["MT", "Malte"], ["NL", "Pays-Bas"],
  ["PL", "Pologne"], ["PT", "Portugal"], ["CZ", "République tchèque"], ["RO", "Roumanie"],
  ["SK", "Slovaquie"], ["SI", "Slovénie"], ["SE", "Suède"],
];
const HORS_UE: [string, string][] = [
  ["MC", "Monaco"], ["CH", "Suisse"], ["GB", "Royaume-Uni"], ["NO", "Norvège"], ["AD", "Andorre"],
  ["MA", "Maroc"], ["TN", "Tunisie"], ["DZ", "Algérie"], ["SN", "Sénégal"], ["CI", "Côte d'Ivoire"],
  ["CA", "Canada"], ["US", "États-Unis"],
];
const EST_UE = new Set(UE.map(([code]) => code));

function Champ({ id, libelle, valeur, onChange, type = "text", requis = false, aide }: {
  id: string; libelle: string; valeur: string | null; onChange: (v: string) => void; type?: string;
  requis?: boolean; aide?: string;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{libelle}{requis && " *"}</label>
      <input id={id} className="input" type={type} value={valeur ?? ""} onChange={(e) => onChange(e.target.value)} />
      {aide && <p className="muted" style={{ marginTop: "0.3rem" }}>{aide}</p>}
    </div>
  );
}

export function FicheClient({ titre = "Fiche client", onEnregistree }: {
  titre?: string; onEnregistree?: () => void;
}) {
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [fait, setFait] = useState(false);

  useEffect(() => {
    api.ficheClient().then((f) => setFiche({ ...VIDE, ...f }), setErreur);
  }, []);

  if (!fiche) return erreur ? <Erreur erreur={erreur} /> : <Chargement />;
  const maj = (cle: keyof Fiche) => (v: string) => { setFiche({ ...fiche, [cle]: v }); setFait(false); };
  const societe = fiche.type === "societe";

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>{titre}</h2>
      <p className="muted" style={{ margin: "0 0 0.75rem" }}>
        L'identité imprimée sur vos bons de commande et vos factures. Les documents déjà émis gardent la leur.
      </p>
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        setEnvoi(true);
        setErreur(null);
        api.enregistrerFicheClient(fiche)
          .then((f) => { setFiche({ ...VIDE, ...f }); setFait(true); onEnregistree?.(); }, setErreur)
          .finally(() => setEnvoi(false));
      }}>
        <div className="actions" style={{ margin: 0 }}>
          {(["societe", "particulier"] as const).map((t) => (
            <button key={t} type="button" className={fiche.type === t ? "btn btn--primary" : "btn btn--secondary"}
                    onClick={() => setFiche({ ...fiche, type: t })}>
              {t === "societe" ? "Société" : "Particulier"}
            </button>
          ))}
        </div>
        {societe ? (
          <Champ id="fc-rs" libelle="Raison sociale" requis valeur={fiche.raison_sociale} onChange={maj("raison_sociale")} />
        ) : (
          <div className="row">
            <Champ id="fc-prenom" libelle="Prénom" requis valeur={fiche.prenom} onChange={maj("prenom")} />
            <Champ id="fc-nom" libelle="Nom" requis valeur={fiche.nom} onChange={maj("nom")} />
          </div>
        )}
        <Champ id="fc-adresse" libelle="Adresse" requis valeur={fiche.adresse} onChange={maj("adresse")} />
        <div className="row">
          <Champ id="fc-cp" libelle="Code postal" requis valeur={fiche.code_postal} onChange={maj("code_postal")} />
          <Champ id="fc-ville" libelle="Ville" requis valeur={fiche.ville} onChange={maj("ville")} />
          <div className="field">
            <label className="field-label" htmlFor="fc-pays">Pays *</label>
            <select id="fc-pays" className="select" value={fiche.pays} onChange={(e) => maj("pays")(e.target.value)}>
              <optgroup label="Union européenne">
                {UE.map(([code, nom]) => <option key={code} value={code}>{nom}</option>)}
              </optgroup>
              <optgroup label="Hors Union européenne">
                {HORS_UE.map(([code, nom]) => <option key={code} value={code}>{nom}</option>)}
              </optgroup>
            </select>
          </div>
        </div>
        {/* SIREN et TVA selon le pays (Franck, 08/10/2026) : SIREN pour une
            societe francaise ; TVA intracommunautaire dans l'UE seulement. */}
        {societe && (fiche.pays === "FR" || EST_UE.has(fiche.pays)) && (
          <div className="row">
            {fiche.pays === "FR" && (
              <Champ id="fc-siren" libelle="SIREN" requis valeur={fiche.siren} onChange={maj("siren")}
                     aide="9 chiffres." />
            )}
            <Champ id="fc-tva" libelle="N° de TVA intracommunautaire" valeur={fiche.tva} onChange={maj("tva")}
                   aide={fiche.pays === "FR" ? "Facultatif." : "Facultatif ; avec lui, la TVA française n'est pas facturée."} />
          </div>
        )}
        <div className="row">
          <Champ id="fc-mobile" libelle="Téléphone mobile" type="tel" valeur={fiche.tel_mobile} onChange={maj("tel_mobile")} />
          <Champ id="fc-fixe" libelle="Téléphone fixe" type="tel" valeur={fiche.tel_fixe} onChange={maj("tel_fixe")} />
        </div>
        <p className="muted" style={{ margin: 0 }}>Au moins un des deux téléphones.</p>
        <Champ id="fc-email" libelle="E-mail de facturation" type="email" requis valeur={fiche.email_facturation}
               onChange={maj("email_facturation")} aide="Celui de votre inscription par défaut ; modifiable." />
        <Erreur erreur={erreur} />
        {fait && <div className="alert alert--success" role="status">Fiche client enregistrée.</div>}
        <div className="actions" style={{ marginTop: 0 }}>
          <button type="submit" className="btn btn--primary" disabled={envoi}>
            {envoi ? "Enregistrement…" : "Enregistrer la fiche"}
          </button>
        </div>
      </form>
    </div>
  );
}
